import {Pipe, PipeTransform} from '@angular/core';
import {DatePipe} from '@angular/common';
import {CallCauseUtil} from '@app/shared/utils/call-cause';
import {GtPhoneNumberUtil} from '@app/shared/utils/gt-phone-number.util';
import {WorkingHours} from '@app/shared/working-hours/working-hours.model';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment';
import {CallLogEntryModel} from '../models/call-log-entry.model';
import {absenceActions} from '@app/models/status.constants';
import {ServiceTemplateId} from '@app/models/enums/service-template-id.enum';

@Pipe({name: 'nameAndNumber'})
export class NameAndNumber implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  transform(entry: CallLogEntryModel): string {
    const formattedNumber = GtPhoneNumberUtil.toDisplay(entry.fullNumber);
    if (entry.caller && entry.caller.length) {
      return `${formattedNumber} (${this.translateService.instant(entry.caller)})`;
    } else {
      return formattedNumber;
    }
  }
}

@Pipe({name: 'shortDate'})
export class ShortDatePipe implements PipeTransform {
  transform(value: Date | moment.Moment | string): string {
    return moment(value).format('L');
  }
}

@Pipe({name: 'shortDateTime'})
export class ShortDateTimePipe implements PipeTransform {
  transform(value: Date | moment.Moment | string): string {
    return moment(value).format('L LT');
  }
}

@Pipe({name: 'timeAndDateFormatter'})
export class TimeAndDateFormatter implements PipeTransform {
  constructor(private translateService: TranslateService) {
    moment.locale(this.translateService.currentLang);
  }

  transform(value: Date, format?: string): string {
    const date = moment(value);
    if (!value) {
      return '';
    } else if (format) {
      return date.format(format);
    } else if (moment().year() === date.year()) {
      if (moment().dayOfYear() === date.dayOfYear()) {
        return date.format('HH:mm');
      } else {
        return date.format('DD/MM, HH:mm');
      }
    } else {
      return date.format('DD/MM/YYYY, HH:mm');
    }
  }
}

@Pipe({name: 'timeAndDateFullFormatter'})
export class TimeAndDateFullFormatter implements PipeTransform {
  transform(value: Date): string {
    const date = moment(value);
    return date.format('DD/MM/YYYY, HH:mm');
  }
}

@Pipe({name: 'callCauseString'})
export class CallCauseString implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  transform(logEntry: CallLogEntryModel): string {
    let status = this.translateService.instant(CallCauseUtil.getCallCauseString(logEntry.cause));
    if (logEntry.redirectEntries && logEntry.redirectEntries.length > 1) {
      status += `, ${this.translateService.instant('global.forwarded')}`;
    }
    if (logEntry.cidName === 'CallbackQueue') {
      status += ` (${this.translateService.instant('call_log.callback_queue')})`;
    }
    return status;
  }
}

@Pipe({name: 'forwardingDelay'})
export class ForwardingDelay implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  transform(value: number): string {
    if (value !== 0) {
      return this.translateService.instant('forwarding.after_seconds', {amount: value});
    } else {
      return '';
    }
  }
}

@Pipe({name: 'minuteDurationFormat'})
export class MinuteDurationFormat implements PipeTransform {
  constructor(private datePipe: DatePipe, private translate: TranslateService) {
    moment.locale(this.translate.currentLang);
  }

  transform(value: string): string {
    const days = this.translate.instant('duration.days');
    const hours = this.translate.instant('duration.hours');
    const minutes = this.translate.instant('duration.minutes');
    return (moment.duration(Number.parseInt(value, 10), 'minutes') as any).format(
      'd [' + days + '] h [' + hours + '] m [' + minutes + ']',
      {trim: 'both', usePlural: false}
    );
    // use plural has to be false here, or else it will display "minutt" in norwegian
  }
}

@Pipe({name: 'forwardingText'})
export class ForwardingText implements PipeTransform {
  transform(value: number): string {
    const target = absenceActions.find(e => e.key === value);
    if (target) {
      return target.text;
    } else {
      return '';
    }
  }
}

@Pipe({name: 'workingHourText'})
export class WorkingHourText implements PipeTransform {
  constructor(private datePipe: DatePipe, private translateService: TranslateService) {}

  transform(workingHours: WorkingHours): string {
    if (workingHours.isFree()) {
      return this.translateService.instant('working_hours.free');
    } else {
      return (
        this.datePipe.transform(moment(workingHours.startTime, 'HH:mm').toDate(), 'HH:mm') +
        ' - ' +
        this.datePipe.transform(moment(workingHours.endTime, 'HH:mm').toDate(), 'HH:mm')
      );
    }
  }
}

@Pipe({name: 'timeLocalization'})
export class TimeLocalization implements PipeTransform {
  constructor(private datePipe: DatePipe) {}

  transform(time: string): string {
    return this.datePipe.transform(moment(time, 'HH:mm').toDate(), 'HH:mm');
  }
}

@Pipe({name: 'endTimeFormatter'})
export class EndTimeFormatter implements PipeTransform {
  transform(value: Date): string {
    const date = moment(value);

    if (!value) {
      return '';
    } else if (moment().dayOfYear() === date.dayOfYear()) {
      return date.format('HH:mm');
    } else {
      return date.format('DD/MM HH:mm');
    }
  }
}

@Pipe({name: 'endTimeFullFormatter'})
export class EndTimeFullFormatter implements PipeTransform {
  transform(value: Date): string {
    const date = moment(value);
    return date.format('DD/MM/YYYY HH:mm');
  }
}

@Pipe({name: 'endTimeShortFormatter'})
export class EndTimeShortFormatter implements PipeTransform {
  transform(value: Date): string {
    const date = moment(value);

    if (!value) {
      return '';
    } else if (moment().dayOfYear() === date.dayOfYear()) {
      return date.format('HH:mm');
    } else if (moment().year() === date.year()) {
      return date.format('DD.MM');
    } else {
      return date.format('YYYY');
    }
  }
}

@Pipe({name: 'LocaleDateAndTimeFormat'})
export class LocaleDateAndTimeFormat implements PipeTransform {
  constructor(private translateService: TranslateService) {
    moment.locale(this.translateService.currentLang);
  }

  transform(value: Date): string {
    if (!value) {
      return '';
    }
    const format = 'MMM DD, HH:mm';
    return moment(value).format(format);
  }
}

@Pipe({name: 'phoneNumberFormatter'})
export class PhoneNumberFormatter implements PipeTransform {
  transform(phoneNumber: string): string {
    return GtPhoneNumberUtil.toDisplay(phoneNumber);
  }
}

@Pipe({name: 'durationFormatter'})
export class DurationFormatter implements PipeTransform {
  transform(duration: number, unit: moment.unitOfTime.DurationConstructor = 'seconds', format?: string, trim?: any): string {
    const momentDuration = moment.duration(duration, unit);
    if (format) {
      return (momentDuration as any).format(format, {trim});
    } else {
      return momentDuration.hours() >= 1
        ? (momentDuration as any).format('HH:mm:ss', {trim: false})
        : (momentDuration as any).format('mm:ss', {trim: false});
    }
  }
}

@Pipe({name: 'durationSince', pure: false})
export class DurationSince implements PipeTransform {
  transform(value: Date): string {
    if (value) {
      const duration = new Date().getTime() - value.getTime();
      return (moment.duration(duration, 'milliseconds') as any).format('mm:ss', {trim: false});
    } else {
      return '0';
    }
  }
}

@Pipe({name: 'servicePattern'})
export class ServicePattern implements PipeTransform {
  transform(value: any, ...args): any {
    return ServiceTemplateId[value] || '-';
  }
}
